import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Play, Download, Plus, Trash2, Music2, Clapperboard, RotateCcw, Volume2, Save, MessageSquare, CheckCircle2, Undo2, GitCompare, Zap } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import StingerPanel from "@/components/StingerPanel";

const PX = 60; // pixels per second
const COLORS = ["#00f0ff", "#ff2bd6", "#8b5cf6", "#22c55e", "#f59e0b", "#3b82f6"];

// Visual scene timeline: drag dialogue lines & SFX along a music bed, then render to MP3.
export default function SceneTimeline({ dialogueId, seq, tracks }) {
  const [items, setItems] = useState([]);
  const [sfxLib, setSfxLib] = useState([]);
  const [bg, setBg] = useState("none");
  const [gain, setGain] = useState(0.18);
  const [busy, setBusy] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [initial, setInitial] = useState([]);
  const [selected, setSelected] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [snapName, setSnapName] = useState("");
  const [activeSnap, setActiveSnap] = useState("live");
  const [notes, setNotes] = useState([]);
  const [activeNote, setActiveNote] = useState(null);
  const [showResolved, setShowResolved] = useState(false);
  const [compare, setCompare] = useState(null); // { a, b, urlA, urlB, loading }
  const audioRef = useRef(null);
  const drag = useRef(null);
  const laneRef = useRef(null);

  const stingerItems = (data) => (data.stingers || []).map((s) => ({ ...s, uid: `stinger-${s.id}` }));
  useEffect(() => {
    api.get(`/voice/dialogue/${dialogueId}/timeline`).then(({ data }) => {
      const its = [...data.lines.map((l) => ({ ...l, gain: 1 })), ...stingerItems(data)];
      setItems(its); setInitial(its); setSfxLib(data.sfx);
    }).catch(() => toast.error("Timeline indisponible."));
    api.get(`/voice/dialogue/${dialogueId}/timelines`).then(({ data }) => setSnapshots(data)).catch(() => {});
    api.get(`/voice/dialogue/${dialogueId}/comments`).then(({ data }) => setNotes(data)).catch(() => {});
  }, [dialogueId]);

  const applySnapshot = (snapId) => {
    setActiveSnap(snapId);
    setPreviewUrl(null);
    if (snapId === "live") { setItems(initial); setBg("none"); return; }
    const snap = snapshots.find((x) => x.id === snapId);
    if (!snap) return;
    const lib = [...initial, ...sfxLib.map((x) => ({ ...x, gain: 0.8 }))];
    const rebuilt = snap.items.map((it, i) => {
      const src = lib.find((x) => x.id === it.voice_id);
      return src ? { ...src, start: it.start, gain: it.gain ?? 1, uid: `${it.voice_id}-${i}` } : null;
    }).filter(Boolean);
    setItems(rebuilt);
    setBg(snap.bg || "none");
    setGain(snap.bg_gain ?? 0.18);
    toast.success(`Version « ${snap.name} » chargée.`);
  };

  const saveSnapshot = async () => {
    const name = snapName.trim() || `Version ${snapshots.length + 1}`;
    try {
      const { data } = await api.post(`/voice/dialogue/${dialogueId}/timelines`, { name, ...payload() });
      setSnapshots((arr) => [data, ...arr]);
      setActiveSnap(data.id);
      setSnapName("");
      toast.success(`Version « ${name} » enregistrée.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Enregistrement impossible."); }
  };

  const deleteSnapshot = async (id) => {
    try {
      await api.delete(`/voice/timelines/${id}`);
      setSnapshots((arr) => arr.filter((x) => x.id !== id));
      if (activeSnap === id) setActiveSnap("live");
      toast.success("Version supprimée.");
    } catch { toast.error("Suppression impossible."); }
  };

  const resolveNote = async (n, resolved) => {
    try {
      const { data } = await api.patch(`/voice/shares/${n.token}/comments/${n.id}`, { resolved });
      setNotes((arr) => arr.map((x) => (x.id === n.id ? data : x)));
      setActiveNote(data);
      toast.success(resolved ? "Note marquée résolue ✓" : "Note réouverte.");
    } catch { toast.error("Mise à jour impossible."); }
  };

  const snapPayload = (snapId) => {
    if (snapId === "live") return payload();
    const sn = snapshots.find((x) => x.id === snapId);
    return { items: sn.items, bg: sn.bg || null, bg_gain: sn.bg_gain ?? 0.18 };
  };
  const runCompare = async (a, b) => {
    setCompare({ a, b, loading: true });
    try {
      const [ra, rb] = await Promise.all([a, b].map((id) => api.post(`/voice/dialogue/${dialogueId}/timeline-export`, snapPayload(id), { responseType: "blob" })));
      setCompare({ a, b, urlA: URL.createObjectURL(ra.data), urlB: URL.createObjectURL(rb.data), loading: false });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Comparaison impossible."); setCompare(null); }
  };
  const snapLabel = (id) => (id === "live" ? "Montage en cours" : snapshots.find((x) => x.id === id)?.name || id);
  const openNotes = notes.filter((n) => !n.resolved);
  const visibleNotes = showResolved ? notes : openNotes;

  const seekTo = (t) => { if (audioRef.current) { audioRef.current.currentTime = t; audioRef.current.play().catch(() => {}); } };

  const characters = useMemo(() => [...new Set(items.filter((i) => i.kind === "tts").map((i) => i.character))], [items]);
  const total = Math.max(8, ...items.map((i) => i.start + i.duration)) + 2;

  const onDown = (e, idx) => { setSelected(idx); drag.current = { idx, x0: e.clientX, s0: items[idx].start }; e.currentTarget.setPointerCapture(e.pointerId); };
  const setItemGain = (g) => { if (selected == null) return; setItems((arr) => arr.map((it, i) => (i === selected ? { ...it, gain: g } : it))); setPreviewUrl(null); };
  const onMove = (e) => {
    if (!drag.current) return;
    const { idx, x0, s0 } = drag.current;
    const ns = Math.max(0, Math.round((s0 + (e.clientX - x0) / PX) * 10) / 10);
    setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, start: ns } : it)));
    setPreviewUrl(null);
  };
  const onUp = () => { drag.current = null; };

  const addSfx = (id) => {
    const s = sfxLib.find((x) => x.id === id);
    if (!s) return;
    const last = items.reduce((m, i) => Math.max(m, i.start + i.duration), 0);
    setItems((arr) => [...arr, { id: s.id, kind: "sfx", prompt: s.prompt, duration: s.duration, start: Math.round(Math.max(0, last - s.duration / 2) * 10) / 10, gain: 0.8, uid: `${s.id}-${Date.now()}` }]);
    setPreviewUrl(null);
  };
  const [stingOpen, setStingOpen] = useState(false);
  const emotions = useMemo(() => [...new Set(initial.filter((i) => i.kind === "tts" && i.emotion && i.emotion !== "neutre").map((i) => i.emotion))], [initial]);
  const onStingersInserted = async () => {
    try {
      const { data: tl } = await api.get(`/voice/dialogue/${dialogueId}/timeline`);
      const st = stingerItems(tl);
      setItems((arr) => [...arr.filter((i) => !i.stinger), ...st]);
      setInitial((arr) => [...arr.filter((i) => !i.stinger), ...st]);
      setSfxLib(tl.sfx);
      setPreviewUrl(null);
      setStingOpen(false);
    } catch { /* keep panel */ }
  };
  const remove = (idx) => { setItems((arr) => arr.filter((_, i) => i !== idx)); setSelected(null); setPreviewUrl(null); };

  const payload = () => ({ items: items.map((i) => ({ voice_id: i.id, start: i.start, gain: i.gain })), bg: bg === "none" ? null : bg, bg_gain: gain });
  const render = async (download) => {
    setBusy(download ? "dl" : "preview");
    try {
      const res = await api.post(`/voice/dialogue/${dialogueId}/timeline-export`, payload(), { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (download) {
        const a = document.createElement("a"); a.href = url; a.download = "scene-timeline.mp3"; document.body.appendChild(a); a.click(); a.remove();
        toast.success("Scène rendue et téléchargée ✨");
      } else setPreviewUrl(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Rendu impossible."); }
    finally { setBusy(null); }
  };

  const lanes = [...characters.map((c) => ({ key: c, label: c, filter: (i) => i.kind === "tts" && i.character === c })), { key: "sfx", label: "SFX", filter: (i) => i.kind === "sfx" }];

  return (
    <div className="mt-2 rounded-xl border border-primary/25 bg-primary/[0.03] p-3 space-y-3" data-testid={`scene-timeline-${seq}`}>
      {stingOpen && <StingerPanel dialogueId={dialogueId} emotions={emotions} onInserted={onStingersInserted} seq={seq} />}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[10px] font-mono text-primary flex items-center gap-1.5"><Clapperboard size={12} /> STUDIO DE SCÈNE — glissez les blocs pour les déplacer · {total.toFixed(1)}s</div>
        <div className="flex items-center gap-2">
          <Select onValueChange={addSfx}>
            <SelectTrigger className="h-8 text-xs w-52 bg-background border-border" data-testid={`timeline-add-sfx-${seq}`}><span className="flex items-center gap-1.5"><Plus size={12} /> Ajouter un SFX…</span></SelectTrigger>
            <SelectContent className="glass border-border max-h-64">
              {sfxLib.length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">Aucun SFX généré</div>}
              {sfxLib.map((s) => <SelectItem key={s.id} value={s.id}>{(s.prompt || "SFX").slice(0, 40)} · {s.duration.toFixed(1)}s</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => setStingOpen((o) => !o)} className={`h-8 text-xs gap-1.5 ${stingOpen ? "bg-amber-500/15 border-amber-500/50 text-amber-400" : "border-amber-500/40 text-amber-400 hover:bg-amber-500/10"}`} title="Un stinger Surge XT par réplique émotionnelle (colère, peur, joie, tristesse, murmure, calme), inséré au début de la réplique · 1 crédit" data-testid={`timeline-stingers-${seq}`}>
            <Zap size={12} /> Stingers Surge
          </Button>
          <Button variant="ghost" size="sm" onClick={() => { setItems(initial); setPreviewUrl(null); }} className="h-8 text-xs gap-1" data-testid={`timeline-reset-${seq}`}><RotateCcw size={12} /> Réinitialiser</Button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border bg-background" onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp} ref={laneRef}>
        <div style={{ width: total * PX + 120 }} className="relative">
          <div className="flex h-6 border-b border-border text-[9px] font-mono text-muted-foreground">
            <div className="w-[110px] shrink-0 sticky left-0 bg-background z-10 border-r border-border" />
            <div className="relative flex-1">
              {Array.from({ length: Math.ceil(total) + 1 }).map((_, s) => (
                <span key={s} className="absolute top-1" style={{ left: s * PX }}>{s}s</span>
              ))}
              {visibleNotes.map((n) => (
                <button key={n.id} type="button" onClick={() => { setActiveNote(n); seekTo(n.time); }} title={`${n.resolved ? "[résolue] " : ""}${n.author} @ ${n.time}s — ${n.text}`}
                  className={`absolute -top-0.5 w-3.5 h-3.5 rounded-full border flex items-center justify-center transition-transform hover:scale-125 ${n.resolved ? "bg-emerald-500/80 border-emerald-300/60" : activeNote?.id === n.id ? "bg-amber-400 border-amber-200" : "bg-amber-500/80 border-amber-300/60"}`}
                  style={{ left: n.time * PX - 7, zIndex: 20 }} data-testid={`timeline-note-${n.id}`}>
                  {n.resolved ? <CheckCircle2 size={8} className="text-black" /> : <MessageSquare size={8} className="text-black" />}
                </button>
              ))}
            </div>
          </div>
          {bg !== "none" && (
            <div className="flex h-7 border-b border-border/60">
              <div className="w-[110px] shrink-0 sticky left-0 bg-background z-10 border-r border-border px-2 text-[10px] font-mono text-secondary flex items-center gap-1"><Music2 size={10} /> Musique</div>
              <div className="relative flex-1"><div className="absolute inset-y-1 left-0 right-0 rounded bg-secondary/20 border border-secondary/30" style={{ backgroundImage: "repeating-linear-gradient(90deg, rgba(255,43,214,.25) 0 2px, transparent 2px 6px)" }} /></div>
            </div>
          )}
          {lanes.map((lane, li) => (
            <div key={lane.key} className="flex h-12 border-b border-border/60 last:border-0" data-testid={`timeline-lane-${lane.key}`}>
              <div className="w-[110px] shrink-0 sticky left-0 bg-background z-10 border-r border-border px-2 text-xs font-semibold flex items-center truncate" style={{ color: COLORS[li % COLORS.length] }}>{lane.label}</div>
              <div className="relative flex-1">
                {items.map((it, idx) => lane.filter(it) && (
                  <div
                    key={it.uid || it.id}
                    onPointerDown={(e) => onDown(e, idx)}
                    title={`${it.kind === "tts" ? it.text : it.prompt} — ${it.start.toFixed(1)}s`}
                    className={`absolute top-1.5 h-9 rounded-md border text-[10px] px-2 flex items-center gap-1 cursor-grab active:cursor-grabbing select-none overflow-hidden ${selected === idx ? "ring-2 ring-white/70" : ""}`}
                    style={{ left: it.start * PX, width: Math.max(28, it.duration * PX), background: `${COLORS[li % COLORS.length]}${Math.round(Math.min(1, 0.15 + (it.gain ?? 1) * 0.35) * 255).toString(16).padStart(2, "0")}`, borderColor: `${COLORS[li % COLORS.length]}88` }}
                    data-testid={`timeline-item-${idx}`}
                    data-stinger={it.stinger ? "true" : undefined}
                  >
                    {it.stinger && <Zap size={9} className="text-amber-400 shrink-0" />}
                    <span className="truncate flex-1">{it.kind === "tts" ? it.text : it.prompt}</span>
                    {(it.gain ?? 1) !== 1 && <span className="font-mono text-[9px] opacity-80 shrink-0">{Math.round((it.gain ?? 1) * 100)}%</span>}
                    <button type="button" onPointerDown={(e) => e.stopPropagation()} onClick={() => remove(idx)} className="text-muted-foreground hover:text-destructive shrink-0" data-testid={`timeline-remove-${idx}`}><Trash2 size={10} /></button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {selected != null && items[selected] && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-background px-3 py-2" data-testid={`timeline-gain-panel-${seq}`}>
          <Volume2 size={13} className="text-primary shrink-0" />
          <span className="text-xs truncate max-w-[220px]">{items[selected].kind === "tts" ? `${items[selected].character} : ${items[selected].text}` : items[selected].prompt}</span>
          <Slider value={[items[selected].gain ?? 1]} min={0.1} max={2} step={0.05} onValueChange={(v) => setItemGain(v[0])} className="flex-1 min-w-[140px]" data-testid={`timeline-gain-${seq}`} />
          <span className="font-mono text-xs text-primary w-12 text-right" data-testid={`timeline-gain-value-${seq}`}>{Math.round((items[selected].gain ?? 1) * 100)}%</span>
          <button type="button" onClick={() => setItemGain(1)} className="text-[10px] font-mono text-muted-foreground hover:text-foreground">100%</button>
        </div>
      )}
      {activeNote && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-400/40 bg-amber-500/[0.08] px-3 py-2 text-xs" data-testid={`timeline-note-panel-${seq}`}>
          <MessageSquare size={13} className="text-amber-400 mt-0.5 shrink-0" />
          <div className="flex-1"><span className="font-mono text-amber-300">{activeNote.time.toFixed(1)}s</span> · <span className="font-semibold">{activeNote.author}</span> — {activeNote.text}{activeNote.resolved && <span className="ml-2 text-emerald-400 font-mono text-[10px]">✓ résolue{activeNote.resolved_by ? ` · ${activeNote.resolved_by}` : ""}</span>}</div>
          {activeNote.resolved ? (
            <Button size="sm" variant="ghost" onClick={() => resolveNote(activeNote, false)} className="h-7 text-xs gap-1 text-muted-foreground" data-testid={`timeline-note-reopen-${seq}`}><Undo2 size={12} /> Réouvrir</Button>
          ) : (
            <Button size="sm" onClick={() => resolveNote(activeNote, true)} className="h-7 text-xs gap-1 bg-emerald-600 hover:bg-emerald-500 text-white" data-testid={`timeline-note-resolve-${seq}`}><CheckCircle2 size={12} /> Marquer résolue</Button>
          )}
          <button type="button" onClick={() => setActiveNote(null)} className="text-muted-foreground hover:text-foreground">✕</button>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-background px-3 py-2" data-testid={`timeline-snapshots-${seq}`}>
        <Label className="text-xs flex items-center gap-1.5"><Save size={12} className="text-primary" /> Versions</Label>
        <Select value={activeSnap} onValueChange={applySnapshot}>
          <SelectTrigger className="h-8 text-xs w-48 bg-card border-border" data-testid={`timeline-snapshot-select-${seq}`}><SelectValue /></SelectTrigger>
          <SelectContent className="glass border-border">
            <SelectItem value="live">Montage en cours</SelectItem>
            {snapshots.map((sn) => <SelectItem key={sn.id} value={sn.id}>{sn.name} · {sn.items.length} él.</SelectItem>)}
          </SelectContent>
        </Select>
        {activeSnap !== "live" && <button type="button" onClick={() => deleteSnapshot(activeSnap)} className="text-muted-foreground hover:text-destructive" title="Supprimer cette version" data-testid={`timeline-snapshot-delete-${seq}`}><Trash2 size={13} /></button>}
        <Input value={snapName} onChange={(e) => setSnapName(e.target.value)} placeholder={`Version ${snapshots.length + 1}`} maxLength={60} className="h-8 text-xs w-40 bg-card border-border" data-testid={`timeline-snapshot-name-${seq}`} onKeyDown={(e) => e.key === "Enter" && saveSnapshot()} />
        <Button size="sm" variant="outline" onClick={saveSnapshot} disabled={items.length === 0} className="h-8 border-primary/40 text-primary hover:bg-primary/10 gap-1.5" data-testid={`timeline-snapshot-save-${seq}`}><Save size={12} /> Enregistrer la version</Button>
        {snapshots.length > 0 && (
          <Button size="sm" variant="outline" onClick={() => runCompare(activeSnap, snapshots.find((x) => x.id !== activeSnap)?.id || "live")} className="h-8 border-secondary/40 text-secondary hover:bg-secondary/10 gap-1.5" data-testid={`timeline-compare-${seq}`}><GitCompare size={12} /> Comparer</Button>
        )}
        {notes.length > 0 && (
          <button type="button" onClick={() => setShowResolved((v) => !v)} className="text-[11px] text-amber-300/90 ml-auto hover:underline" data-testid={`timeline-notes-count-${seq}`}>
            {openNotes.length} note{openNotes.length > 1 ? "s" : ""} ouverte{openNotes.length > 1 ? "s" : ""} · {notes.length - openNotes.length} résolue{notes.length - openNotes.length > 1 ? "s" : ""} {showResolved ? "(masquer)" : "(afficher)"}
          </button>
        )}
      </div>
      {compare && (
        <div className="rounded-lg border border-secondary/30 bg-secondary/[0.04] p-3 space-y-2" data-testid={`timeline-compare-panel-${seq}`}>
          <div className="flex items-center justify-between text-xs">
            <span className="font-mono text-secondary flex items-center gap-1.5"><GitCompare size={12} /> COMPARER DEUX VERSIONS</span>
            <button type="button" onClick={() => setCompare(null)} className="text-muted-foreground hover:text-foreground">✕</button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {["a", "b"].map((side) => (
              <div key={side} className="rounded-lg border border-border bg-background p-2.5 space-y-2" data-testid={`timeline-compare-${side}-${seq}`}>
                <Select value={compare[side]} onValueChange={(v) => runCompare(side === "a" ? v : compare.a, side === "b" ? v : compare.b)}>
                  <SelectTrigger className="h-8 text-xs bg-card border-border"><SelectValue /></SelectTrigger>
                  <SelectContent className="glass border-border">
                    <SelectItem value="live">Montage en cours</SelectItem>
                    {snapshots.map((sn) => <SelectItem key={sn.id} value={sn.id}>{sn.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {compare.loading ? <div className="h-10 flex items-center justify-center text-xs text-muted-foreground"><Loader2 className="animate-spin mr-2" size={13} /> Rendu…</div>
                  : <audio src={compare[side === "a" ? "urlA" : "urlB"]} controls className="w-full h-10" data-testid={`timeline-compare-audio-${side}-${seq}`} />}
                <Button size="sm" variant="outline" disabled={compare.loading} onClick={() => { applySnapshot(compare[side]); setCompare(null); }} className="h-7 text-xs w-full border-primary/40 text-primary hover:bg-primary/10" data-testid={`timeline-compare-keep-${side}-${seq}`}>
                  Garder « {snapLabel(compare[side])} »
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs flex items-center gap-1.5"><Music2 size={11} className="text-secondary" /> Lit musical (Librairie)</Label>
          <Select value={bg} onValueChange={(v) => { setBg(v); setPreviewUrl(null); }}>
            <SelectTrigger className="h-8 text-xs bg-background border-border" data-testid={`timeline-bg-${seq}`}><SelectValue /></SelectTrigger>
            <SelectContent className="glass border-border max-h-64">
              <SelectItem value="none">Aucun</SelectItem>
              {tracks.map((t) => <SelectItem key={t.id} value={t.id}>#{t.seq} · {t.metadata?.title || t.prompt.slice(0, 40)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {bg !== "none" && (
          <div className="space-y-1">
            <div className="flex justify-between text-xs"><Label>Volume musique</Label><span className="font-mono text-secondary">{Math.round(gain * 100)}%</span></div>
            <Slider value={[gain]} min={0.05} max={0.6} step={0.01} onValueChange={(v) => { setGain(v[0]); setPreviewUrl(null); }} />
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => render(false)} disabled={!!busy || items.length === 0} className="h-8 border-primary/40 text-primary hover:bg-primary/10 gap-1.5" data-testid={`timeline-preview-${seq}`}>
          {busy === "preview" ? <Loader2 className="animate-spin" size={13} /> : <Play size={13} />} Écouter le montage
        </Button>
        <Button size="sm" onClick={() => render(true)} disabled={!!busy || items.length === 0} className="h-8 bg-secondary text-white hover:bg-secondary/90 gap-1.5" data-testid={`timeline-download-${seq}`}>
          {busy === "dl" ? <Loader2 className="animate-spin" size={13} /> : <Download size={13} />} Exporter le montage (MP3)
        </Button>
        <span className="text-[11px] text-muted-foreground">{items.length} éléments · {characters.length} personnages</span>
      </div>
      {previewUrl && <audio ref={audioRef} src={previewUrl} controls autoPlay className="w-full h-10" data-testid={`timeline-audio-${seq}`} />}
    </div>
  );
}
